import { useEffect, useState } from 'react';
import { adminApi } from '../../../services/adminApi';

interface GameRow {
  id: string;
  name: string;
  status: 'waiting' | 'in_progress';
  createdAt: string;
  startedAt: string | null;
  maxPlayers: number;
  smallBlind: string;
  bigBlind: string;
  players: Array<{
    userId: string;
    username: string;
    email: string;
    seatIndex: number;
    chipStack: string;
    position: string;
  }>;
}

function micro(v: string): string {
  return (Number(v) / 1_000_000).toFixed(2);
}

export default function GamesTab() {
  const [games, setGames] = useState<GameRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function refresh() {
    const res = await adminApi.get('/games');
    setGames(res.data.games);
  }

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, []);

  async function cancel(g: GameRow) {
    const reason = prompt(`Cancel "${g.name}"? Players will be refunded. Reason:`);
    if (!reason) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await adminApi.post(`/games/${g.id}/cancel`, { reason });
      setMsg(
        `Game cancelled. ${res.data.playersRefunded} player(s) refunded ${
          Number(res.data.totalRefunded) / 1_000_000
        } mUSD total.`
      );
      refresh();
    } catch (e: any) {
      setMsg(e.response?.data?.message ?? 'Cancel failed');
    } finally {
      setBusy(false);
    }
  }

  async function cleanup() {
    if (!confirm('Cleanup all stuck games (>10min in_progress or >24h waiting)?')) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await adminApi.post('/games/cleanup');
      setMsg(
        `Cleanup OK. ${res.data.gamesMarkedCancelled} game(s) closed, ${res.data.playersRefunded} player(s) refunded.`
      );
      refresh();
    } catch (e: any) {
      setMsg(e.response?.data?.message ?? 'Cleanup failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold">Live games ({games.length})</h2>
        <button
          disabled={busy}
          onClick={cleanup}
          className="px-3 py-2 text-sm bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 rounded-lg disabled:opacity-50"
        >
          Cleanup stuck games
        </button>
      </div>

      {msg && (
        <div className="text-sm bg-white/5 border border-white/10 rounded px-3 py-2">{msg}</div>
      )}

      <div className="space-y-3">
        {games.map((g) => (
          <div key={g.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold">
                  {g.name}{' '}
                  <span
                    className={`ml-2 text-xs px-2 py-0.5 rounded ${
                      g.status === 'in_progress'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                    }`}
                  >
                    {g.status}
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  Blinds {micro(g.smallBlind)}/{micro(g.bigBlind)} \u00b7 {g.players.length}/{g.maxPlayers} players
                  \u00b7 created {new Date(g.createdAt).toLocaleString()}
                </div>
              </div>
              <button
                disabled={busy}
                onClick={() => cancel(g)}
                className="px-3 py-1.5 text-xs bg-red-600/30 hover:bg-red-600/40 border border-red-500/50 rounded-lg disabled:opacity-50"
              >
                Cancel & refund
              </button>
            </div>
            {g.players.length > 0 && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                {g.players.map((p) => (
                  <div
                    key={p.userId}
                    className="flex justify-between bg-black/20 rounded px-2 py-1"
                  >
                    <span className="text-gray-300">
                      seat {p.seatIndex} \u00b7 {p.username}{' '}
                      <span className="text-gray-500">({p.position})</span>
                    </span>
                    <span className="tabular-nums">{micro(p.chipStack)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {games.length === 0 && (
          <div className="text-center text-gray-400 py-8">No active games.</div>
        )}
      </div>
    </div>
  );
}
